import * as React from "react"

import type { DrawResult, PageGeometry, PageSection } from "./pdf-surface"

export function DrawLayer({
  geom,
  sections,
  onComplete,
  onCancel,
}: {
  geom: PageGeometry
  sections: PageSection[]
  onComplete: (r: DrawResult) => void
  onCancel: () => void
}): JSX.Element {
  const [drag, setDrag] = React.useState<{
    x0: number
    y0: number
    x1: number
    y1: number
  } | null>(null)

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault()
        setDrag(null)
        onCancel()
      }
    }
    window.addEventListener("keydown", onKey)
    return () => {
      window.removeEventListener("keydown", onKey)
    }
  }, [onCancel])

  // Measured off the element the handler is bound to, not off a ref: every
  // caller is a pointer handler on the layer itself, so currentTarget is that
  // element and cannot be null while the event is being delivered.
  //
  // In the layer's own pixels (the page's at zoom 1), not the screen's: the
  // page may be scaled, so the on-screen rect is read only for the ratio
  // between the two.
  const localPoint = (e: React.PointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const toLocalX = rect.width > 0 ? geom.cssWidth / rect.width : 1
    const toLocalY = rect.height > 0 ? geom.cssHeight / rect.height : 1
    return {
      x: Math.max(0, Math.min(geom.cssWidth, (e.clientX - rect.left) * toLocalX)),
      y: Math.max(0, Math.min(geom.cssHeight, (e.clientY - rect.top) * toLocalY)),
    }
  }

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    e.currentTarget.setPointerCapture(e.pointerId)
    const { x, y } = localPoint(e)
    setDrag({ x0: x, y0: y, x1: x, y1: y })
  }

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag) return
    const { x, y } = localPoint(e)
    setDrag((d) => (d ? { ...d, x1: x, y1: y } : d))
  }

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag) return
    e.currentTarget.releasePointerCapture(e.pointerId)
    const scaleX = geom.cssWidth / geom.pointWidth
    const scaleY = geom.cssHeight / geom.pointHeight
    const x0 = Math.min(drag.x0, drag.x1) / scaleX
    const y0 = Math.min(drag.y0, drag.y1) / scaleY
    const x1 = Math.max(drag.x0, drag.x1) / scaleX
    const y1 = Math.max(drag.y0, drag.y1) / scaleY
    setDrag(null)
    if (x1 - x0 < 8 || y1 - y0 < 8) return
    const yMid = (y0 + y1) / 2
    const section = sections.find((s) => yMid >= s.y0 && yMid < s.y1) ?? null
    onComplete({
      bbox: { page: geom.page, x0, y0, x1, y1 },
      section,
    })
  }

  const box = drag
    ? {
        left: Math.min(drag.x0, drag.x1),
        top: Math.min(drag.y0, drag.y1),
        width: Math.abs(drag.x1 - drag.x0),
        height: Math.abs(drag.y1 - drag.y0),
      }
    : null

  return (
    <div
      role="application"
      aria-label="Draw a box to add a finding"
      className="absolute inset-0 cursor-crosshair"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {box && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute rounded-[2px] border-[1.5px] border-dashed border-[color:var(--brand)] bg-[color:var(--brand)]/10"
          style={box}
        >
          {box.width > 40 && box.height > 20 && (
            <span className="absolute -bottom-5 right-0 rounded-sm bg-[color:var(--brand)] px-1.5 py-0.5 font-id text-[9.5px] font-medium tabular-nums text-white shadow-sm">
              {Math.round(box.width / (geom.cssWidth / geom.pointWidth))}×
              {Math.round(box.height / (geom.cssHeight / geom.pointHeight))} pt
            </span>
          )}
        </div>
      )}
    </div>
  )
}
